import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CustomTableRow from "./CustomTableRow";

/**
 * CustomTable
 *
 * Column def shape:
 *  { label, field, sortable?, align?, stickyFirst?, stickyLast?, render?, sx?, headerSx? }
 *
 * Props:
 *  columns      – column definitions array
 *  rows         – data rows array
 *  sortConfig   – { key, direction: "asc"|"desc" }
 *  onSort       – (field: string) => void
 *  tableSx      – extra sx forwarded to TableContainer
 *  emptyMessage – shown when rows is empty
 */
const CustomTable = ({
  columns = [],
  rows = [],
  sortConfig = { key: null, direction: "asc" },
  onSort,
  tableSx = {},
  emptyMessage = "No Record Found",
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ── Colours ──────────────────────────────────────────────────────────────
  const borderClr = theme.palette.divider;

  // Opaque sticky header background
  const headStickyBg = isDark ? "#1a1d2e" : "#f4f6fb";

  // Opaque sticky body background (same as paper)
  const bodyStickyBg = theme.palette.background.paper;

  // Opaque hover colour blended against paper (no alpha so sticky cells stay solid)
  const hoverSolid = isDark ? "#2a3148" : "#eef1f8";
  // Opaque hover for sticky cells (slightly more distinct)
  const hoverStickySolid = isDark ? "#22263a" : "#e8ecf6";
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        overflowX: "auto",
        border: `1px solid ${borderClr}`,
        borderRadius: 2,
        ...tableSx,
      }}
    >
      <Table
        stickyHeader
        size="medium"          /* compact row height */
        sx={{
          tableLayout: "auto",

          /* universal cell border */
          "& .MuiTableCell-root": {
            borderRight: `1px solid ${borderClr}`,
            borderBottom: `1px solid ${borderClr}`,
            verticalAlign: "middle",
            /* tight padding for compact rows */
            py: "10px",
            px: "12px",
          },
          "& .MuiTableCell-root:last-of-type": {
            borderRight: "none",
          },
        }}
      >
        {/* ── HEAD ─────────────────────────────────────────────────────── */}
        <TableHead>
          <TableRow>
            {columns.map((col, idx) => {
              const isStickyFirst = !!col.stickyFirst;
              const isStickyLast = !!col.stickyLast;
              const align = col.align || "center";
              const headerAlign = col.headerAlign || align;

              return (
                <TableCell
                  key={idx}
                  align={headerAlign}
                  sortDirection={
                    col.sortable && sortConfig.key === col.field
                      ? sortConfig.direction
                      : false
                  }
                  sx={{
                    whiteSpace: "nowrap",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: theme.palette.text.secondary,
                    backgroundColor: headStickyBg,
                    height: 60,
                    minHeight: 60,
                    py: 0,

                    /* sticky-first (plan_name) */
                    ...(isStickyFirst && {
                      position: "sticky",
                      left: 0,
                      zIndex: 6,
                      borderRight: `2px solid ${borderClr}`,
                      backgroundColor: headStickyBg,
                    }),

                    /* sticky-last (action) */
                    ...(isStickyLast && {
                      position: "sticky",
                      right: 0,
                      zIndex: 6,
                      borderLeft: `2px solid ${borderClr}`,
                      borderRight: "none",
                      backgroundColor: headStickyBg,
                    }),

                    ...col.headerSx,
                  }}
                >
                  {col.sortable && onSort ? (
                    <TableSortLabel
                      active={sortConfig.key === col.field}
                      direction={
                        sortConfig.key === col.field ? sortConfig.direction : "asc"
                      }
                      onClick={() => onSort(col.field)}
                      sx={{
                        width: "100%",
                        justifyContent:
                          align === "center"
                            ? "center"
                            : align === "right"
                              ? "flex-end"
                              : "flex-start",
                        "& .MuiTableSortLabel-icon": {
                          opacity: sortConfig.key === col.field ? 1 : 0.3,
                        },
                        "&:hover .MuiTableSortLabel-icon": { opacity: 1 },
                        color: "inherit",
                        "&.Mui-active": { color: theme.palette.primary.main },
                      }}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>

        {/* ── BODY ─────────────────────────────────────────────────────── */}
        <TableBody>
          {rows?.length ? (
            rows.map((row, idx) => (
              <CustomTableRow
                key={row.id ?? idx}
                row={row}
                columns={columns}
                isLast={idx === rows.length - 1}
                bodyStickyBg={bodyStickyBg}
                hoverSolid={hoverSolid}
                hoverStickySolid={hoverStickySolid}
                borderClr={borderClr}
              />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ borderRight: "none" }}
              >
                <Typography variant="body2" color="text.disabled">
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTable;